import { useState, useEffect, useCallback, useRef } from 'react';

import {
  getLeaveReconciliationBulkJobs,
  getLeaveReconciliationBulkJobDownload,
  cancelLeaveReconciliationBulkJob,
  createLeaveReconciliationBulkJob,
} from '../../../services/leaveReconciliationService';
import { isActiveJob, finishedSince } from './bulk';

export const POLL_INTERVAL_MS = 4000;

// Kullanıcının kendi toplu işleri (sunucu son 20'yi döner). Etkin bir iş varken
// yoklanır — çekmece kapalıyken de, çünkü araç çubuğundaki rozet ve iş bitince
// tablonun tazelenmesi buna bağlı. Yoklama zincirlenir (setTimeout): yavaş bir
// yanıt bir sonrakiyle ÜST ÜSTE binmez. Bir yoklama başarısız olursa yoklama DURUR
// (bağlantı kopukken her 4 sn'de bir hata basmasın); çekmeceyi açmak yeniden dener.
export default function useBulkJobs({ enabled, onJobsFinished }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const jobsRef = useRef([]);
  const onFinishedRef = useRef(onJobsFinished);
  const mountedRef = useRef(true);

  useEffect(() => { onFinishedRef.current = onJobsFinished; }, [onJobsFinished]);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const res = await getLeaveReconciliationBulkJobs();
    if (!mountedRef.current) return false;
    if (!silent) setLoading(false);
    // api.js GET okumalarında reject ETMEZ — gerçek sinyal res.data.status.
    if (!res?.data?.status) {
      setFailed(true);
      return false;
    }
    const next = Array.isArray(res.data.data) ? res.data.data : [];
    const finished = finishedSince(jobsRef.current, next);
    jobsRef.current = next;
    setJobs(next);
    setFailed(false);
    if (finished.length > 0) onFinishedRef.current?.(finished);
    return true;
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const hasActive = jobs.some(isActiveJob);

  useEffect(() => {
    if (!enabled || !hasActive || failed) return undefined;
    let stopped = false;
    let timer;
    const tick = async () => {
      await load({ silent: true });
      if (!stopped) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [enabled, hasActive, failed, load]);

  // Başarı/hata toast'ı interceptor'dadır (sunucu `bulkJobQueued` / `bulkJobInProgress`).
  const start = useCallback(async (request) => {
    try {
      const res = await createLeaveReconciliationBulkJob(request);
      if (!res?.data?.status) return false;
      await load({ silent: true });
      return true;
    } catch {
      return false;
    }
  }, [load]);

  // İmzalı adres 15 dk geçerlidir ve dosyanın indirme adı sunucuda yazılıdır
  // (Content-Disposition: attachment) — bağlantıya tıklamak sayfadan ayrılmadan indirir.
  const download = useCallback(async (job) => {
    const res = await getLeaveReconciliationBulkJobDownload(job.id);
    const url = res?.data?.status ? res.data.data?.url : null;
    if (!url) {
      // Süresi az önce dolmuş olabilir — satırın "indirilebilir" hâli tazelensin.
      await load({ silent: true });
      return false;
    }
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  }, [load]);

  const cancel = useCallback(async (job) => {
    try {
      await cancelLeaveReconciliationBulkJob(job.id);
    } catch {
      // Hata toast'ı interceptor'da; liste yine tazelenir (iş bu arada bitmiş olabilir).
    }
    await load({ silent: true });
  }, [load]);

  return {
    jobs, loading, failed, hasActive, load, start, download, cancel,
  };
}
